import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useContext, useEffect, useState } from "react";
import LightCalculator from "../../LightCalculator/LightCalculator";
import JobsPanel from "../../JobsPanel/JobsPanel";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../../store/rootReducer";

import LightbulbIcon from "../../../assets/lightCalculation/lightbulb.svg?react";
import BriefcaseIcon from "../../../assets/jobs/briefcase.svg?react";
import PencilIcon from "../../../assets/versionControl/pencil.svg?react";
import SectorIcon from "../../../assets/sectorLines.svg?react";
import OverrettIcon from "../../../assets/mountains.svg?react";

import "../PanelContent.css";
import { setRightPanelActiveTab, setRightPanelOpen } from "../../../store/appSlice";
import { RightPanelTabs } from "../../../constants/Panels";
import FeatureEditor from "../../FeatureEditor/FeatureEditor";
import SectorEditor from "../../MapTools/LightHouse/SectorEditor";
import { AppContext, VersionContext } from "../../../Context";
import EFSManager from "../../EFSManager/EFSManager";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import OverettEditor from "../../MapTools/Overett/OverettEditor";
import MorildExport from "../../MorildExport/MorildExport";
import SeaCableEditor from "../../MapTools/SeaCable/SeaCableEditor";
import { SksdLog } from "../../SKSD/SksdLog";
import DataImportTabs from "../../DataImport/DataImportTabs";

const RightPanelContent = () => {
    const dispatch = useDispatch();

    const [activeIndex, setActiveIndex] = useState<number>(-1);

    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const versionContext = useContext(VersionContext);
    const versionServiceRef = versionContext?.versionServiceRef.value;
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const sectorEditorIsOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditorOpen);
    const isCreatingOverett = useSelector((state: StoreState) => state.overett.isCreatingOverett);
    const isEditingOverett = useSelector((state: StoreState) => state.overett.isEditingOverett);

    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;

    const [renderedTabs, setRenderedTabs] = useState<{ [key: number]: boolean }>({});
    const handleTabSelect = (index: number) => {
        setActiveIndex(index);
        setRenderedTabs((prev) => ({ ...prev, [index]: true }));
    };

    const checkIfDefaultVersion = () => {
        if (activeVersion && versionServiceRef?.current) {
            return isDefaultVersion(activeVersion, versionServiceRef.current);
        }
        return false;
    };

    useEffect(() => {
        switch (rightPanelActiveTab) {
            case RightPanelTabs.LIGHT_CALCULATOR:
                setActiveIndex(0);
                break;
            case RightPanelTabs.JOBS:
                setActiveIndex(1);
                break;
            case RightPanelTabs.EDITOR:
                setActiveIndex(2);
                break;
            case RightPanelTabs.SECTOR:
                setActiveIndex(3);
                break;
            case RightPanelTabs.OVERETT:
                setActiveIndex(4);
                break;
            case RightPanelTabs.EFS_MANAGER:
                setActiveIndex(5);
                break;
            case RightPanelTabs.MORILD_EXPORT:
                setActiveIndex(6);
                break;
            case RightPanelTabs.SEA_CABLE:
                setActiveIndex(7);
                break;
            case RightPanelTabs.DATA_IMPORT:
                setActiveIndex(8);
                break;
            case RightPanelTabs.SKSD:
                setActiveIndex(9);
                break;
            default:
                setActiveIndex(-1);
                break;
        }
    }, [rightPanelActiveTab]);

    useEffect(() => {
        if (activeIndex === -1) {
            dispatch(setRightPanelOpen(false));
        } else {
            dispatch(setRightPanelOpen(true));
        }
    }, [activeIndex]);

    useEffect(() => {
        if (!checkIfDefaultVersion()) {
            if (activeIndex === 5) {
                dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            }
        }
    }, [activeVersion]);

    return (
        <div className="panel-content">
            <Tabs
                className={`w-100 h-100 flex-col`}
                selectedIndex={activeIndex}
                disabledTabClassName="disabled"
                selectedTabClassName="selected"
                onSelect={handleTabSelect}
            >
                <TabList className={`panel-content-tab-list hidden`}>
                    <Tab className={`panel-content-tab-button`} title="Lysberegning">
                        <LightbulbIcon />
                    </Tab>
                    <Tab className={`panel-content-tab-button`} title="Jobber">
                        <BriefcaseIcon />
                    </Tab>
                    <Tab disabled={selectedFeature === null} title="Rediger" className={`panel-content-tab-button`}>
                        <PencilIcon />
                    </Tab>
                    <Tab disabled={!sectorEditorIsOpen} title="Skjerming" className={`panel-content-tab-button`}>
                        <SectorIcon />
                    </Tab>
                    <Tab
                        disabled={!isCreatingOverett && !isEditingOverett}
                        className={`panel-content-tab-button`}
                        title="Overett"
                    >
                        <OverrettIcon />
                    </Tab>
                    <Tab disabled={!checkIfDefaultVersion()} className={`panel-content-tab-button`}>
                        EFS
                    </Tab>
                    <Tab
                        disabled={!checkIfDefaultVersion() || true}
                        title="Deaktivert - Under arbeid"
                        className={`panel-content-tab-button`}
                    >
                        Morild
                    </Tab>
                    <Tab title="Sjøkabel" className={`panel-content-tab-button`}>
                        Sjøkabel
                    </Tab>
                    <Tab title="Dataimport" className={`panel-content-tab-button`}>
                        Dataimport
                    </Tab>
                    <Tab title="SKSD logg" className={`panel-content-tab-button`}>
                        SKSD logg
                    </Tab>
                </TabList>
                <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`} forceRender={true}>
                    {/* Keep it rendered to be able to fill out all relevant fields when opening */}
                    <LightCalculator />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[1] === true}
                >
                    <JobsPanel />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[2] === true}
                >
                    <FeatureEditor />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[3] === true}
                >
                    <SectorEditor />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[4] === true}
                >
                    <OverettEditor />
                </TabPanel>
                <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`} forceRender={true}>
                    {/* Keep it rendered to avoid losing work */}
                    <EFSManager />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[6] === true}
                >
                    <MorildExport />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[7] === true}
                >
                    <SeaCableEditor readonly={checkIfDefaultVersion()} />
                </TabPanel>
                <TabPanel
                    selectedClassName="selected"
                    className={`panel-content-tab-panel`}
                    forceRender={renderedTabs[8] === true}
                >
                    <DataImportTabs />
                </TabPanel>
                <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`}>
                    <SksdLog />
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default RightPanelContent;
